import { lazy, Suspense, useState, useRef, useEffect, useCallback, useMemo } from "react";
import type { ComponentType } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { PreloadLink as Link } from "@/components/PreloadLink";
import { preloadRouteChunk, preloadMenuTargetsAtIdle, preloadImage } from "@/lib/preload-routes";
import { Menu, X, ChevronDown } from "lucide-react";
import { withStaleChunkRecovery } from "@/lib/lazy-with-reload";

// MobileDrawer is rendered via createPortal only when the user
// taps the hamburger menu — Lighthouse never opens it. React.lazy
// keeps the drawer code (including its FacebookIcon, social-links
// list, focus-trap, and ~5 KB of nav markup) out of the entry
// chunk until the user actually requests the menu.
//
// withStaleChunkRecovery wraps the import so a stale chunk after a
// deploy triggers a guarded full-page reload instead of a Sentry
// "Failed to fetch dynamically imported module" error
// (ORBRASIL-FRONTEND-8 / 2).
const MobileDrawer = lazy(withStaleChunkRecovery(() => import("./MobileDrawer")));
import type { MobileNavItem } from "./MobileDrawer";
// SearchTypeahead pulls lib/search → spec-code-index → ~85 KB of
// static spec data. We keep the skeleton mounted until the lazy
// chunk *fully* resolves (ImportSearchTypeahead does this). Using
// React.lazy + Suspense would unmount the skeleton input on first
// interaction, which loses keystrokes mid-flight (Playwright's
// fill() races the React re-render). See SearchTypeaheadSkeleton.tsx.
import SearchTypeaheadSkeleton from "./SearchTypeaheadSkeleton";
import type SearchTypeaheadType from "./SearchTypeahead";

type SearchTypeaheadModule = { default: typeof SearchTypeaheadType };
let searchTypeaheadPromise: Promise<SearchTypeaheadModule> | null = null;
const loadSearchTypeahead = (): Promise<SearchTypeaheadModule> => {
  if (!searchTypeaheadPromise) {
    searchTypeaheadPromise = import("./SearchTypeahead");
  }
  return searchTypeaheadPromise;
};
import { Button } from "@/components/ui/button";
import BrandLogo from "./BrandLogo";

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// CMS-driven dropdown items
import { productLines, productFamilies } from "@/lib/data";
import { useDataVersion } from "@/hooks/useDataVersion";
import type { ProductFamily } from "@/lib/data";
import FadeImage from "@/components/ui/FadeImage";
import { smallSlotSources, smallSlotSrc } from "@/lib/image-sources";
import { getFamilyUrl } from "@/lib/productUrl";
import { whatsappContactUrl } from "@/lib/whatsapp";

interface DropdownMenuItem {
  name: string;
  slug: string;
  /** Where this entry actually goes — see lib/productUrl.getFamilyUrl. */
  href: string;
  description: string;
  /** Family hero image. Already resolved for the open-submenu preload. */
  image?: string;
  imageSources?: ProductFamily["imageSources"];
}

interface DropdownMenuProps {
  id: string;
  label: string;
  to: string;
  items: DropdownMenuItem[];
  activeSubmenu: string | null;
  openSubmenu: (id: string) => void;
  closeSubmenu: () => void;
  /** Synchronous close — fired on item click for instant visual feedback. */
  closeSubmenuNow: () => void;
  isCurrentPage: boolean;
}

const DropdownMenu = ({ id, label, to, items, activeSubmenu, openSubmenu, closeSubmenu, closeSubmenuNow, isCurrentPage }: DropdownMenuProps) => {
  const isOpen = activeSubmenu === id;
  const menuRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openSubmenu(id);
        // Focus first menu item after render
        setTimeout(() => {
          const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
          first?.focus();
        }, 0);
      }
      return;
    }

    const menuItems = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    if (!menuItems || menuItems.length === 0) return;
    const currentIndex = Array.from(menuItems).findIndex(el => el === document.activeElement);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        menuItems[(currentIndex + 1) % menuItems.length].focus();
        break;
      case "ArrowUp":
        e.preventDefault();
        menuItems[(currentIndex - 1 + menuItems.length) % menuItems.length].focus();
        break;
      case "Escape":
        e.preventDefault();
        closeSubmenu();
        // Return focus to trigger
        (e.currentTarget as HTMLElement).querySelector<HTMLElement>("a")?.focus();
        break;
      case "Home":
        e.preventDefault();
        menuItems[0].focus();
        break;
      case "End":
        e.preventDefault();
        menuItems[menuItems.length - 1].focus();
        break;
    }
  }, [isOpen, id, openSubmenu, closeSubmenu]);

  return (
    <div
      className="relative"
      onMouseEnter={() => openSubmenu(id)}
      onMouseLeave={closeSubmenu}
      onKeyDown={handleKeyDown}
    >
      <Link
        to={to}
        className="px-2 xl:px-3 py-2 text-foreground hover:text-primary transition-colors font-medium rounded-lg hover:bg-primary/5 flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-current={isCurrentPage ? "page" : undefined}
        onFocus={() => openSubmenu(id)}
        onClick={closeSubmenuNow}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </Link>
      {isOpen && (
        <div ref={menuRef} className="absolute top-full left-0 pt-2 w-80" role="menu">
          <div className="bg-card rounded-xl border border-border shadow-xl p-2 animate-fade-in">
            {items.map((item) => (
              <Link
                key={item.slug}
                to={item.href}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/5 active:bg-primary/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:bg-primary/5"
                role="menuitem"
                tabIndex={-1}
                onClick={closeSubmenuNow}
              >
                {/* Family artwork, left-aligned. These images are already
                    warmed by the open-submenu preload below, so showing them
                    here costs no extra request. `contain` on a muted tile —
                    the products are shot on white and cropping a 12-square
                    thumbnail out of them would show nothing recognisable.
                    aria-hidden + empty alt: the link is already labelled by
                    the name and description beside it. */}
                <div
                  aria-hidden="true"
                  // WHITE, like the tiles: the image below is object-contain,
                  // so a product shot that isn't square letterboxes and the
                  // backing colour shows as bands. Product photography is on
                  // white, so grey reads as a band bolted onto the product.
                  className="shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-white"
                >
                  {item.image && item.image !== "/placeholder.svg" ? (
                    <FadeImage
                      src={smallSlotSrc(item.imageSources, item.image)}
                      sources={smallSlotSources(item.imageSources)}
                      alt=""
                      sizes="48px"
                      objectFit="contain"
                      className="w-full h-full"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-muted to-muted/40" />
                  )}
                </div>
                <div className="min-w-0">
                  {/* Wraps rather than truncates — the thumbnail eats enough
                      width that "Rebitadeiras Hidropneumáticas" would clip
                      mid-word, and a family name is what the reader is
                      scanning for. */}
                  <p className="font-medium text-foreground leading-snug">{item.name}</p>
                  {item.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SobreDropdown = ({ activeSubmenu, openSubmenu, closeSubmenu, closeSubmenuNow, isCurrentPage }: { activeSubmenu: string | null; openSubmenu: (id: string) => void; closeSubmenu: () => void; closeSubmenuNow: () => void; isCurrentPage: boolean }) => {
  const isOpen = activeSubmenu === 'sobre';
  const menuRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openSubmenu('sobre');
        setTimeout(() => {
          menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        }, 0);
      }
      return;
    }

    const menuItems = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    if (!menuItems || menuItems.length === 0) return;
    const currentIndex = Array.from(menuItems).findIndex(el => el === document.activeElement);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        menuItems[(currentIndex + 1) % menuItems.length].focus();
        break;
      case "ArrowUp":
        e.preventDefault();
        menuItems[(currentIndex - 1 + menuItems.length) % menuItems.length].focus();
        break;
      case "Escape":
        e.preventDefault();
        closeSubmenu();
        (e.currentTarget as HTMLElement).querySelector<HTMLElement>("a")?.focus();
        break;
    }
  }, [isOpen, openSubmenu, closeSubmenu]);

  return (
    <div
      className="relative"
      onMouseEnter={() => openSubmenu('sobre')}
      onMouseLeave={closeSubmenu}
      onKeyDown={handleKeyDown}
    >
      <Link
        to="/sobre"
        className="px-2 xl:px-3 py-2 text-foreground hover:text-primary transition-colors font-medium rounded-lg hover:bg-primary/5 flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-current={isCurrentPage ? "page" : undefined}
        onFocus={() => openSubmenu('sobre')}
        onClick={closeSubmenuNow}
      >
        Sobre
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </Link>
      {isOpen && (
        <div ref={menuRef} className="absolute top-full left-0 pt-2 w-56" role="menu">
          <div className="bg-card rounded-xl border border-border shadow-xl p-2 animate-fade-in">
            <Link to="/sobre" className="block px-4 py-3 rounded-lg hover:bg-primary/5 active:bg-primary/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:bg-primary/5" role="menuitem" tabIndex={-1} onClick={closeSubmenuNow}>
              <p className="font-medium text-foreground">Sobre a Or Brasil</p>
              <p className="text-sm text-muted-foreground">História, certificações e equipe</p>
            </Link>
            <Link to="/eventos" className="block px-4 py-3 rounded-lg hover:bg-primary/5 active:bg-primary/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:bg-primary/5" role="menuitem" tabIndex={-1} onClick={closeSubmenuNow}>
              <p className="font-medium text-foreground">Eventos e Feiras</p>
              <p className="text-sm text-muted-foreground">Participação em feiras industriais</p>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

// MobileDrawer + its supporting types live in `./MobileDrawer` and
// load lazily on first hamburger tap. See the React.lazy import at
// the top of this file.

// ── Main Header ───────────────────────────────────────────────────

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  // The real SearchTypeahead chunk pulls in ~85 KB of static spec data
  // via lib/search → spec-code-index. We render only the skeleton on
  // first paint (keeps the chunk out of Lighthouse's "unused JS" tally
  // and saves bytes for visitors who never search). When the user
  // interacts (focus / hover / typing), we kick off the dynamic
  // import — the skeleton stays mounted until the import RESOLVES.
  // Using React.lazy + Suspense would unmount the skeleton's input
  // immediately on first render, causing a re-mount that loses
  // mid-typing keystrokes (Playwright's `fill()` races the React
  // re-render and ends up with an empty value). Stashing the resolved
  // component into local state after `await import(...)` guarantees a
  // single, atomic swap that preserves typed text via `initialQuery`.
  const [SearchTypeaheadComp, setSearchTypeaheadComp] =
    useState<ComponentType<React.ComponentProps<typeof SearchTypeaheadType>> | null>(null);
  const [pendingQuery, setPendingQuery] = useState("");
  const triggerSearchLoad = useCallback(() => {
    if (SearchTypeaheadComp) return;
    loadSearchTypeahead()
      .then((mod) => setSearchTypeaheadComp(() => mod.default))
      .catch(() => {
        // The import path is content-hashed and immutable; a failure
        // generally means a deploy-during-session stale chunk. Fall
        // back silently — the skeleton's form still submits searches.
      });
  }, [SearchTypeaheadComp]);
  // Skeleton form-submit (Enter key, GO button) navigates even before
  // the lazy chunk arrives — important for fast users and the e2e
  // suite that fires `fill + press("Enter")` synchronously.
  const navigate = useNavigate();
  const submitSearch = useCallback(
    (q: string) => {
      const trimmed = q.trim();
      if (trimmed.length < 2) return;
      navigate(`/busca?q=${encodeURIComponent(trimmed)}`);
      setPendingQuery("");
    },
    [navigate],
  );

  const openSubmenu = useCallback((id: string) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setActiveSubmenu(id);
  }, []);

  const closeSubmenu = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => setActiveSubmenu(null), 150);
  }, []);

  // Used on CLICK (vs hover-leave): collapse the menu synchronously
  // so the click is acknowledged visually at the moment it fires.
  // Without this, the menu stayed open for ~150 ms while navigation
  // started under it, which felt like input lag — the user couldn't
  // tell whether their click had registered.
  const closeSubmenuNow = useCallback(() => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setActiveSubmenu(null);
  }, []);

  // Header stays mounted for the whole visit (it lives in the router's site
  // shell, see routes-client.tsx), so a remount no longer re-reads the CMS
  // data. The version has to be in the menu's deps below, or the menu keeps
  // the build-time snapshot after the live fetch lands (see Events.tsx).
  const dataVersion = useDataVersion();

  // Build dropdown items from CMS data. We also pin the family's hero
  // image and its responsive variants so the submenu-open prefetch can
  // warm both the route chunk AND the hero artwork at the right size —
  // without imageSources the preload targets the widest variant, but
  // the destination's hero renders at 320px and srcset picks card_2x,
  // so the preload misses and a second fetch fires after React commits.
  const lineItemsMap = useMemo(() => {
    type Item = {
      name: string;
      slug: string;
      href: string;
      description: string;
      image?: string;
      imageSources?: ProductFamily["imageSources"];
    };
    const map: Record<string, Item[]> = {};
    for (const line of productLines) {
      map[line.slug] = productFamilies
        .filter((f) => f.lineId === line.id)
        .map((f) => ({
          name: f.name,
          slug: f.slug,
          // Resolved once here so the link, the hover preload and the
          // family page's own redirect all agree on the destination.
          // Skips a family page that would only repeat its own name in a
          // single group card — see lib/productUrl.getFamilyUrl.
          href: getFamilyUrl(f) ?? `/produtos/${line.slug}/${f.slug}`,
          // "Menu Bar Description" is optional in the CMS, so this can be
          // blank — the panel simply drops the line rather than repeating
          // the family name. No hard 60-char slice any more either: it cut
          // mid-word, and the panel now clamps to two lines in CSS.
          description: f.description ?? "",
          image: f.image,
          imageSources: f.imageSources,
        }));
    }
    return map;
  }, [dataVersion]);

  // Bundled into one useMemo so the three slot arrays share a stable
  // reference identity across renders. The naïve form
  //   const fixadoresItems = lineItemsMap["fixadores"] ?? [];
  // creates a fresh `[]` on every render whenever the lookup misses,
  // which made the `[activeSubmenu, *Items]` useEffect below re-fire
  // on every header render — including the high-frequency scroll
  // re-render that updates `--site-header-h` — causing wasted route-
  // chunk preload calls and image preloads.
  const { fixadoresItems, ferramentasItems, servicosItems } = useMemo(
    () => ({
      fixadoresItems: lineItemsMap["fixadores"] ?? [],
      ferramentasItems: lineItemsMap["ferramentas"] ?? [],
      servicosItems: lineItemsMap["servicos"] ?? [],
    }),
    [lineItemsMap],
  );

  // Eagerly preload every child route the moment a submenu opens.
  // Why: when the user is browsing the home page the hover-to-click
  // window is ~500-800 ms (they explore the panel). On a content
  // page they switch context fast — hover→click can be ~100-150 ms,
  // shorter than a fresh chunk fetch. By the time the dropdown is
  // visible, all child chunks are already in flight, so the click
  // never has to wait. Browser de-duplicates dynamic imports so
  // calling this on every open is free.
  //
  // We ALSO preload each family's hero image. The route chunk loading
  // alone is necessary but not sufficient — the destination page's
  // <img> still has to round-trip after React commits, leaving a
  // brief visual gap. Pre-warming the image with <link rel=preload>
  // closes that gap so the new page renders with art already in the
  // HTTP cache.
  useEffect(() => {
    if (!activeSubmenu) return;
    const items =
      activeSubmenu === "fixadores" ? fixadoresItems
      : activeSubmenu === "ferramentas" ? ferramentasItems
      : activeSubmenu === "servicos" ? servicosItems
      : null;
    if (!items) return;
    for (const item of items) {
      preloadRouteChunk(item.href);
      // Match the family hero's display dimensions (HeroAccent renders
      // a 320px circular accent on lg, 192px on mobile). imagesrcset
      // lets the browser pick the same variant that <img srcset> will
      // pick at click time — usually card_2x at 2x DPR desktop.
      preloadImage(item.image, item.imageSources, "(min-width: 1024px) 320px, 192px");
    }
  }, [activeSubmenu, fixadoresItems, ferramentasItems, servicosItems]);

  // Idle-time preload of the heavy menu-target route chunks. Doesn't
  // wait for a hover — by the time the user goes for the menu, the
  // chunks have already loaded silently. The on-submenu-open prefetch
  // above is still kept as a backstop for first-paint-fast users
  // (e.g. someone who hovers Fixadores within 50 ms of the page
  // becoming interactive).
  useEffect(() => {
    preloadMenuTargetsAtIdle();
  }, []);

  // Publish the live header height as --site-header-h on the document
  // root so other sticky elements (e.g. the discontinued banner on the
  // product page) can offset themselves correctly. The header is
  // sticky top-0 z-50; without this CSS var those siblings get
  // covered. Resize observer keeps the value accurate when the header
  // grows (mobile menu open, breakpoint flip).
  useEffect(() => {
    const headerEl = document.querySelector("header");
    if (!headerEl) return;
    const apply = () => {
      const h = headerEl.getBoundingClientRect().height;
      if (h > 0) document.documentElement.style.setProperty("--site-header-h", `${Math.round(h)}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(headerEl);
    return () => ro.disconnect();
  }, []);

  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMobileMenuOpen]);

  // Gate aria-current behind a post-mount flag. Reason: SSR's
  // StaticRouter and the client's BrowserRouter both have the
  // correct pathname at first render, so isActive() *should* return
  // the same value on both sides. Empirically, hydrating a deep
  // route (e.g. /produtos/<line>/<family>/<group>/<product>) under
  // hydrateRoot fires React's "Extra attributes from the server:
  // aria-current" warning on the prefix-matched nav Link — most
  // likely a react-router-dom 6.30.3 quirk where the Link
  // forwards aria-current through a context-derived path that
  // resolves slightly differently between renderToString and
  // hydration's first pass. Computing isActive() always-false on
  // the first render lets SSR and first-client render produce the
  // same DOM (no aria-current). After mount, the real value flips
  // in. UX cost: a single extra render to add the active-state
  // attribute, which the user doesn't perceive.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  const isActive = useCallback((path: string) => {
    if (!hasMounted) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }, [location.pathname, hasMounted]);

  // Mobile nav items with hierarchical structure
  const mobileNavItems: MobileNavItem[] = useMemo(() => [
    { label: "Home", to: "/" },
    {
      label: "Fixadores", to: "/produtos/fixadores",
      children: fixadoresItems.map(i => ({ label: i.name, to: `/produtos/fixadores/${i.slug}`, description: i.description })),
    },
    {
      label: "Ferramentas", to: "/produtos/ferramentas",
      children: ferramentasItems.map(i => ({ label: i.name, to: `/produtos/ferramentas/${i.slug}`, description: i.description })),
    },
    {
      label: "Serviços", to: "/servicos",
      children: servicosItems.length > 0 ? servicosItems.map(i => ({ label: i.name, to: `/servicos/${i.slug}`, description: i.description })) : undefined,
    },
    {
      label: "Sobre", to: "/sobre",
      children: [
        { label: "Sobre a Or Brasil", to: "/sobre", description: "História, certificações e equipe" },
        { label: "Eventos e Feiras", to: "/eventos", description: "Participação em feiras industriais" },
      ],
    },
  ], [fixadoresItems, ferramentasItems, servicosItems]);

  return (
    <>
    <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-xl border-b border-border/50 shadow-sm">
      <div className="container-industrial py-3">
        <div className="flex items-center justify-between gap-3">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg" aria-label="Or Brasil Fixação">
            <BrandLogo className="h-10 md:h-12 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5" aria-label="Navegação principal">
            <Link
              to="/"
              className="px-2 xl:px-3 py-2 text-foreground hover:text-primary transition-colors font-medium rounded-lg hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-current={isActive('/') ? 'page' : undefined}
            >
              Home
            </Link>

            <DropdownMenu id="fixadores" label="Fixadores" to="/produtos/fixadores" items={fixadoresItems} activeSubmenu={activeSubmenu} openSubmenu={openSubmenu} closeSubmenu={closeSubmenu} closeSubmenuNow={closeSubmenuNow} isCurrentPage={isActive('/produtos/fixadores')} />
            <DropdownMenu id="ferramentas" label="Ferramentas" to="/produtos/ferramentas" items={ferramentasItems} activeSubmenu={activeSubmenu} openSubmenu={openSubmenu} closeSubmenu={closeSubmenu} closeSubmenuNow={closeSubmenuNow} isCurrentPage={isActive('/produtos/ferramentas')} />
            <DropdownMenu id="servicos" label="Serviços" to="/servicos" items={servicosItems} activeSubmenu={activeSubmenu} openSubmenu={openSubmenu} closeSubmenu={closeSubmenu} closeSubmenuNow={closeSubmenuNow} isCurrentPage={isActive('/servicos')} />
            <SobreDropdown activeSubmenu={activeSubmenu} openSubmenu={openSubmenu} closeSubmenu={closeSubmenu} closeSubmenuNow={closeSubmenuNow} isCurrentPage={isActive('/sobre') || isActive('/eventos')} />
          </nav>

          {/* Right side: Search + Contato.
              Between lg and xl (1024–1279px) the full row doesn't fit: logo,
              five nav items, search, WhatsApp and Contato need ~1090px and
              the container leaves ~950 at 1024, so the row ran off the right
              edge and the whole page scrolled sideways. Below xl the nav
              links and Contato lose some padding, the search box narrows to
              w-32 with its placeholder hidden and no grow-on-focus (see
              SearchTypeahead), and WhatsApp drops out; the footer and the
              contact page still link it. xl and up is the original header.
              tests/e2e/header-fit.spec.ts guards every one of these widths. */}
          <div className="hidden lg:flex items-center gap-3">
            {SearchTypeaheadComp ? (
              <SearchTypeaheadComp
                variant="desktop"
                initialQuery={pendingQuery}
                autoFocus
              />
            ) : (
              <SearchTypeaheadSkeleton
                variant="desktop"
                value={pendingQuery}
                onChange={setPendingQuery}
                onActivate={triggerSearchLoad}
                onSubmit={submitSearch}
              />
            )}

            <Button asChild size="icon" variant="whatsapp" className="hidden xl:inline-flex rounded-full border-0 shadow-sm focus-visible:ring-primary">
              <a href={whatsappContactUrl()} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                <WhatsAppIcon className="w-4 h-4" />
              </a>
            </Button>
            <Button asChild className="rounded-full px-4 xl:px-6 bg-surface-dark-elevated hover:bg-surface-dark-hover text-white border-0 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
              <Link to="/contato" aria-current={isActive('/contato') ? 'page' : undefined}>
                Contato
              </Link>
            </Button>
          </div>

          {/* Mobile: Hamburger */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              ref={hamburgerRef}
              className="p-2 text-foreground hover:bg-primary/5 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

      </div>
    </header>

    {/* Mobile search bar — styled to blend with hero */}
    <div className="lg:hidden bg-surface-dark px-4 py-3">
      {SearchTypeaheadComp ? (
        <SearchTypeaheadComp
          variant="mobile"
          initialQuery={pendingQuery}
          autoFocus
        />
      ) : (
        <SearchTypeaheadSkeleton
          variant="mobile"
          value={pendingQuery}
          onChange={setPendingQuery}
          onActivate={triggerSearchLoad}
          onSubmit={submitSearch}
        />
      )}
    </div>

      {/* Mobile Drawer — portaled to body to escape header's
          backdrop-blur stacking context. Wrapped in Suspense
          because the drawer module is dynamically imported; the
          fallback is null since the drawer is itself an overlay
          (no layout shift while the chunk lands — typically
          <100 ms on a warm cache). */}
      {isMobileMenuOpen && createPortal(
        <Suspense fallback={null}>
          <MobileDrawer
            isOpen={isMobileMenuOpen}
            onClose={() => {
              setIsMobileMenuOpen(false);
              hamburgerRef.current?.focus();
            }}
            navItems={mobileNavItems}
            isActive={isActive}
          />
        </Suspense>,
        document.body
      )}
    </>
  );
};

export default Header;
